import { IconCash, IconGraph, IconUser } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'

import { cases } from '@subaio/utils'

import type { Step, StepType } from 'containers/Flow/Step'
import { type StepHandlers, UserType } from 'containers/Flow/stepHandler'

import { ButtonSolid } from 'components/Buttons'
import { ItemCard } from 'components/ItemCard'
import { Card } from 'components/Layout'

import { ButtonGroup } from '../ButtonGroup'
import { Container } from '../Container'
import { Wrapper } from '../Wrapper'

type Props = TaggedType<Step, StepType.Introduction> & {
	onNext: StepHandlers['onIntroductionNext']
}

export const Introduction = ({ budgetId, brandMetadata, brandFeatures, userMetadata, onNext }: Props) => {
	const { advisorName, applicantName, userName, coapplicantName, userType, isPrimary } = userMetadata

	return (
		<Wrapper>
			<Card>
				<Container>
					<h1>
						<FormattedMessage id='flow.introduction.title' defaultMessage='Hello {userName}' values={{ userName }} />
					</h1>
					<p>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage
									id='flow.introduction.description'
									defaultMessage='Your advisor, {advisorName}, needs an overview of your finances in order to process your loan application.'
									values={{ advisorName }}
								/>
							),
							[UserType.PrimaryWithCoapplicant]: (
								<FormattedMessage
									id='flow.introduction.descriptionWithCoapplicant'
									defaultMessage='Your advisor, {advisorName}, needs an overview of your finances in order to process your application.'
									values={{ advisorName }}
								/>
							),
							[UserType.Coapplicant]: (
								<FormattedMessage
									id='flow.introduction.descriptionCoapplicant'
									defaultMessage='Your advisor, {advisorName}, needs an overview of your finances in order to process your loan application.'
									values={{ advisorName }}
								/>
							),
						})}
					</p>
					<p>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage
									id='flow.introduction.disclaimer'
									defaultMessage='By following the simple steps below, you can easily submit your budget so that {advisorName} can quickly process your application.'
									values={{ advisorName }}
								/>
							),
							[UserType.PrimaryWithCoapplicant]: (
								<FormattedMessage
									id='flow.introduction.disclaimerWithCoapplicant'
									defaultMessage='By following the simple steps below, you and {coapplicantName} can easily submit your budget so {advisorName} can quickly process your application.'
									values={{ advisorName, coapplicantName }}
								/>
							),
							[UserType.Coapplicant]: (
								<FormattedMessage
									id='flow.introduction.disclaimerCoapplicant'
									defaultMessage='By following the simple steps below, you can easily submit your budget so {advisorName} can quickly process your application.'
									values={{ advisorName }}
								/>
							),
						})}
					</p>
					<ItemCard
						icon={<IconCash />}
						title={<FormattedMessage id='flow.introduction.stepOne' defaultMessage='Step 1' />}
						subtitle={<FormattedMessage id='flow.introduction.stepOneTitle' defaultMessage='Acquisition of data' />}
					>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage
									id='flow.introduction.stepOneDescription'
									defaultMessage='We start by collecting data from your bank(s) and any credit cards. This helps us get an overall picture of your financial situation.'
								/>
							),
							[UserType.PrimaryWithCoapplicant]: (
								<>
									<p>
										<FormattedMessage
											id='flow.introduction.stepOneDescriptionWithCoapplicant'
											defaultMessage='We start by collecting data from your bank(s) and any credit cards. This helps us get an overall picture of your financial situation.'
										/>
									</p>
									<div>
										<b>
											<FormattedMessage
												id='flow.introduction.stepOneDescriptionPrimaryNoticeTitle'
												defaultMessage='Beware'
											/>
										</b>
									</div>
									<FormattedMessage
										id='flow.introduction.stepOneDescriptionPrimaryNotice'
										defaultMessage='Before you can proceed to step 2, {coapplicantName} must have completed the flow via the link provided.'
										values={{ coapplicantName }}
									/>
								</>
							),
							[UserType.Coapplicant]: (
								<FormattedMessage
									id='flow.introduction.stepOneDescriptionCoapplicant'
									defaultMessage='We start by collecting data from your bank(s) and any credit cards. This helps us get an overall picture of your financial situation.'
								/>
							),
						})}
					</ItemCard>
					<ItemCard
						icon={<IconUser />}
						title={<FormattedMessage id='flow.introduction.stepTwo' defaultMessage='Step 2' />}
						subtitle={<FormattedMessage id='flow.introduction.stepTwoTitle' defaultMessage='Adjustment of budget' />}
					>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage
									id='flow.introduction.stepTwoDescription'
									defaultMessage='After the information has been collected, you get a financial overview, which you can review and adjust as necessary. Your review ensures that the budget reflects your current situation as well as possible.'
								/>
							),
							[UserType.PrimaryWithCoapplicant]: (
								<FormattedMessage
									id='flow.introduction.stepTwoDescriptionWithCoapplicant'
									defaultMessage="After the information is gathered, you' ll be given a financial overview that you'll review together and adjust as needed. Your review will ensure that the budget reflects your current situation as closely as possible."
								/>
							),
							[UserType.Coapplicant]: (
								<FormattedMessage
									id='flow.introduction.stepTwoDescriptionCoapplicant'
									defaultMessage='After your information is collected, it will be transferred to {applicantName} so that it can be included in your joint budget, which you will finalize together and send to the financial institution by {applicantName}.'
									values={{ applicantName }}
								/>
							),
						})}
					</ItemCard>
					{isPrimary && (
						<ItemCard
							icon={<IconGraph />}
							title={<FormattedMessage id='flow.introduction.stepThree' defaultMessage='Step 3' />}
							subtitle={<FormattedMessage id='flow.introduction.stepThreeTitle' defaultMessage='Submit budget' />}
						>
							{cases(userType, {
								[UserType.Default]: (
									<FormattedMessage
										id='flow.introduction.stepThreeDescription'
										defaultMessage='Once you are happy with the budget, it will be sent to {advisorName} who will review it. You will be contacted shortly to discuss the next steps in the process.'
										values={{ advisorName }}
									/>
								),
								[UserType.PrimaryWithCoapplicant]: (
									<FormattedMessage
										id='flow.introduction.stepThreeDescriptionWithCoapplicant'
										defaultMessage="Once you're happy with the budget, send it to {advisorName} who will review it. You will be contacted shortly to discuss the next steps in the process."
										values={{ advisorName }}
									/>
								),
							})}
						</ItemCard>
					)}
				</Container>
				<ButtonGroup>
					<ButtonSolid
						label={<FormattedMessage id='flow.buttonNext' defaultMessage='Continue' />}
						onClick={() => onNext(budgetId, brandMetadata, brandFeatures, userMetadata)}
					/>
				</ButtonGroup>
			</Card>
		</Wrapper>
	)
}
